import { useCallback, useEffect, useRef, useState } from 'react';
import {
    AlertBox,
    Box,
    Button,
    FlexBox,
    FormInputGroup,
    FormLabel,
    H2,
    Typography,
    useScreenClass,
} from '@vp/swan';
import { TimezoneSelector, useIdentityContext } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { usePage } from '@99designs/tracking';
import { useDesignLiveMyDesignerFeatureFlag } from '../../../../../lib/experiments';
import { useBriefFormContext } from '../../../../BriefContext/BriefFormContext';
import { useEditModal } from '../../../Review/EditModalContext';
import { useIsEditMode } from '../../../ViewContext';
import { Field_ClientConfiguredField_Fragment } from '../../../brief.generated';
import { useTrackFieldChange } from '../../../useTrackFieldChange';
import { ClientConfiguredFieldProps } from '../types';
import DateSelector from './DateSelector';
import { DesignLiveSessionConfirmation } from './DesignLiveSessionConfirmation';
import DesignerSelector from './DesignerSelector';
import TimeSelector from './TimeSelector';
import {
    ANY_DESIGNER_CALENDAR_ID,
    parseAppointmentValue,
    serializeAppointmentValue,
} from './appointmentValue';
import { isAppointmentValid } from './isAppointmentValid';
import { useConfirmDesignLiveTimeIsAvailable } from './useConfirmDesignLiveTimeIsAvailable';
import { useDesignLiveDesignerSelection } from './useDesignLiveDesignerSelection';
import { useUpdateDesignLiveAppointment } from './useUpdateDesignLiveAppointment';

const designLiveUnavailableErrorMessage = __(
    'Your selected DesignLive session is no longer available. Please select a new day and time.',
    {
        _context:
            'Validation error message when the selected appointment time is no longer available',
    }
);

// clientConfiguredValue is stored as either:
//   Legacy: '2023-08-14T00:00:00Z'  (bare ISO string)
//   New:    '{"time":"2026-07-02T10:00:00","timezone":"Europe/Kyiv"}' (JSON envelope)
// parseAppointmentValue handles both formats
export function Input({
    id,
    clientConfiguredValue,
    setValue,
    register,
    immutable,
}: ClientConfiguredFieldProps) {
    const { locale } = usePage();
    const { isSignedIn, SignIn } = useIdentityContext();
    const { isFeatureGateEnabled: isMyDesignerEnabled, isLoading: isMyDesignerFlagLoading } =
        useDesignLiveMyDesignerFeatureFlag();

    const parsed = parseAppointmentValue(clientConfiguredValue);
    const initialTimezone = parsed.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Convert the initial time to the user's local timezone and format it as an ISO date string (YYYY-MM-DD)
    const localIsoTime = parsed.time
        ? new Date(parsed.time).toLocaleDateString('en-CA', {
              timeZone: initialTimezone,
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
          })
        : null;

    const initialCalendarId = parsed.calendarId ?? ANY_DESIGNER_CALENDAR_ID;
    const initialDesignerName = parsed.designerName;

    const [timezone, setTimezone] = useState(initialTimezone);
    const [date, setDateDispatcher] = useState<string | null>(localIsoTime);
    const [time, setTime] = useState(parsed.time);
    const { briefId } = useBriefFormContext();
    const isEditMode = useIsEditMode();

    const trackTimezoneChange = useTrackFieldChange('timezone');
    const trackDateChange = useTrackFieldChange('appointmentDate');
    const trackTimeChange = useTrackFieldChange('appointmentTime');
    const updateMutation = useUpdateDesignLiveAppointment(id, briefId, parsed.time);
    // Avoid re-persisting an unchanged appointment.
    const lastPersistedRef = useRef<string | null>(parsed.time ? clientConfiguredValue : null);

    // Clears a slot invalidated by designer changes.
    const clearSelectedSlot = useCallback(() => {
        setDateDispatcher(null);
        setTime('');
    }, []);

    const {
        previousDesigners,
        isLoadingDesigners,
        hasDesignersError,
        matchWithPreviousDesigner,
        matchWithPreviousDesignerChoice,
        calendarId,
        effectiveCalendarId,
        selectedDesignerName,
        onToggleChange,
        onCalendarChange,
    } = useDesignLiveDesignerSelection({
        isSignedIn,
        enabled: isMyDesignerEnabled,
        initialCalendarId,
        initialDesignerName,
        initialMatchWithPreviousDesigner: parsed.matchWithPreviousDesigner,
        hasExistingAppointment: Boolean(parsed.time),
        onSelectionCleared: clearSelectedSlot,
    });

    const { checkAvailability } = useConfirmDesignLiveTimeIsAvailable(
        date,
        time,
        timezone,
        effectiveCalendarId
    );

    const handleTimezoneChange = (newTimezone: string) => {
        setTimezone(newTimezone);
        setDateDispatcher(null);
        setTime('');
        trackTimezoneChange();
    };

    const setDate = (newDate: string | null) => {
        // Only clear time when the user picks a different date, not when re-selecting a time in the same date
        const isSameDay = date && newDate && date === newDate;
        if (!isSameDay) {
            setTime('');
            trackDateChange();
        }
        setDateDispatcher(newDate);
    };

    const handleTimeChange = (newTime: string) => {
        setTime(newTime);
        trackTimeChange();
    };

    useEffect(() => {
        register(id, {
            required: __('Please select a date and time', {
                _context:
                    'Validation error message when user has not selected a date and time for an appointment',
            }),
            validate: async (value) => {
                // Validate the time inside either stored format.
                const { time: appointmentTime } = parseAppointmentValue(value);
                if (!appointmentTime || isNaN(new Date(appointmentTime).getTime())) {
                    return __('Please select a date and time');
                }

                // If editing from cart, we want to check if the selected time is in at least 140 mins in the past.
                // Since the user has already booked out this time, we cannot use checkAvailability to validate.
                if (isEditMode) {
                    if (!isAppointmentValid(appointmentTime)) {
                        return designLiveUnavailableErrorMessage;
                    }
                    return true;
                }

                const isAvailable = await checkAvailability();
                if (isAvailable === false) {
                    return designLiveUnavailableErrorMessage;
                }

                if (isAvailable === null) {
                    return __(
                        'There was an error checking the availability of your selected DesignLive session. Please try again.',
                        {
                            _context:
                                'Validation error message when there is an error checking appointment time availability',
                        }
                    );
                }
                return true;
            },
        });
    }, [checkAvailability, id, isEditMode, register]);

    useEffect(() => {
        // Loading state would strip a saved designer.
        if (isMyDesignerFlagLoading) {
            return;
        }

        // Preserve designer selection in form round-trips.
        const serialized = time
            ? serializeAppointmentValue(
                  time,
                  timezone,
                  effectiveCalendarId,
                  selectedDesignerName,
                  matchWithPreviousDesignerChoice
              )
            : '';
        // Persist changes while always registering the form value.
        if (serialized !== lastPersistedRef.current) {
            lastPersistedRef.current = serialized;
            void updateMutation(serialized);
        }
        setValue(id, serialized, { shouldValidate: Boolean(time) });
    }, [
        id,
        time,
        timezone,
        effectiveCalendarId,
        selectedDesignerName,
        matchWithPreviousDesignerChoice,
        isMyDesignerFlagLoading,
        setValue,
        updateMutation,
    ]);

    return (
        <Box pb={8} data-testid={`field-${id}`}>
            {time && (
                <Box marginBottom="6" data-testid={`field-${id}-appointment-time-display`}>
                    <DesignLiveSessionConfirmation
                        time={time}
                        timezone={timezone}
                        designerName={selectedDesignerName}
                    />
                </Box>
            )}
            <Box data-testid={`field-${id}-designer-selector`}>
                {isMyDesignerEnabled && (
                    <DesignerSelector
                        id={`${id}-designer`}
                        isSignedIn={isSignedIn}
                        onSignIn={() =>
                            SignIn(undefined, { trackingCaller: 'design-live-designer-selection' })
                        }
                        matchWithPreviousDesigner={matchWithPreviousDesigner}
                        onToggleChange={onToggleChange}
                        designers={previousDesigners}
                        isLoadingDesigners={isLoadingDesigners}
                        hasDesignersError={hasDesignersError}
                        calendarId={calendarId}
                        setCalendarId={onCalendarChange}
                        immutable={immutable}
                    />
                )}
            </Box>
            <H2 fontSkin="title-subsection" mb={5}>
                {__('Choose a date and time', {
                    _context:
                        'Heading for question on a form for customer to make an appointment with a designer',
                })}
            </H2>
            <FormLabel fontSkin="body-standard-bold" htmlFor={id} marginBottom={'3'}>
                {__('Your timezone')}
            </FormLabel>
            <FormInputGroup>
                <TimezoneSelector
                    value={timezone}
                    onChange={handleTimezoneChange}
                    browserTimezone={initialTimezone}
                    locale={locale}
                    data-testid={`field-${id}-timezone-selector`}
                />
            </FormInputGroup>
            <div>
                <Box marginTop="5" data-testid={`field-${id}-date-selector`}>
                    <FormLabel fontSkin="body-standard-bold" htmlFor={id} marginBottom={'3'}>
                        {__('Select a date', {
                            _context: 'Label for a date selector input on a form',
                        })}
                    </FormLabel>
                    <FormInputGroup>
                        <DateSelector
                            timezone={timezone}
                            setDate={setDate}
                            date={date}
                            immutable={immutable}
                            calendarId={effectiveCalendarId}
                        />
                    </FormInputGroup>
                </Box>
                {date && isDateFormattedCorrectly(date) && (
                    <Box marginTop="5" data-testid={`field-${id}-time-selector`}>
                        <FormLabel fontSkin="body-standard-bold" htmlFor={id} marginBottom={'3'}>
                            {__('Select a time', {
                                _context: 'Label for a time selector input on a form',
                            })}
                        </FormLabel>
                        <TimeSelector
                            timezone={timezone}
                            id={id}
                            time={time}
                            setTime={handleTimeChange}
                            date={date}
                            immutable={immutable}
                            calendarId={effectiveCalendarId}
                        />
                        {isSignedIn && matchWithPreviousDesigner && (
                            <Typography fontSkin="body-small" textColor="subtle" mt={2}>
                                {selectedDesignerName
                                    ? __('Showing times when {{designerName}} is available', {
                                          designerName: selectedDesignerName,
                                          _context:
                                              'Helper text under the time picker when a specific designer is selected',
                                      })
                                    : __("Showing times across all designer's availability", {
                                          _context:
                                              'Helper text under the time picker when the customer is being matched with any available designer',
                                      })}
                            </Typography>
                        )}
                    </Box>
                )}
            </div>
        </Box>
    );
}

export function Review({ clientConfiguredValue, id }: Field_ClientConfiguredField_Fragment) {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const {
        time,
        timezone: parsedTimezone,
        designerName,
    } = parseAppointmentValue(clientConfiguredValue);
    const timezone = parsedTimezone || browserTimezone;

    const { open } = useEditModal();
    const screenClass = useScreenClass();
    const isXs = screenClass === 'xs';
    const rawTime = time || null;

    if (rawTime && !isAppointmentValid(rawTime)) {
        return (
            <AlertBox style={{ width: '100%' }} skin="error">
                <FlexBox
                    alignItems={isXs ? 'flex-start' : 'center'}
                    justifyContent={'space-between'}
                    flexDirection={isXs ? 'column' : 'row'}
                >
                    <Typography fontSkin="body-small" fontSize="small">
                        {__('Your selected DesignLive session is no longer available.', {
                            _context:
                                'Validation error message when the selected appointment time is no longer available',
                        })}
                    </Typography>
                    <Button skin="link" onClick={open} fontSkin={'body-standard-bold'}>
                        {__('Please select a new day and time', {
                            _context:
                                'link to go back to appointment question when the selected appointment time is no longer available',
                        })}
                    </Button>
                </FlexBox>
            </AlertBox>
        );
    }

    return (
        <div data-testid={`field-readonly-${id}`}>
            {time && (
                <DesignLiveSessionConfirmation
                    time={time}
                    timezone={timezone}
                    designerName={designerName}
                />
            )}
        </div>
    );
}

// Check if the date is in the format YYYY-MM-DD
function isDateFormattedCorrectly(date: string) {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    return regex.test(date);
}
